/**
 * One set of unit constants and conversions for StatsView and the Report Builder.
 *
 * Both products used to keep their own copy of these factors, and the copies had already
 * drifted by a digit (acres per square metre). Callers that need the historical builder
 * acre factor read M2_PER_AC; everyone else reads AC_TO_METERS2. Splitting the two names
 * keeps a merge from moving a published number.
 */

export const KM_TO_MI = 0.621371;
export const M_TO_FT = 3.28084;
/** Alias used by the builder's row-spacing labels. Same factor as M_TO_FT. */
export const FT_PER_M = M_TO_FT;
/** Kilometres per hour to miles per hour. Same factor as KM_TO_MI, kept as its own name. */
export const KPH_TO_MPH = KM_TO_MI;
export const MPH_TO_KPH = 1 / KPH_TO_MPH;
export const AC_TO_HA = 0.404686;
export const HA_TO_AC = 2.47105;
export const AC_TO_METERS2 = 4046.856422;
export const HA_TO_METERS2 = 10000;
/**
 * Square metres per acre as the builder's adapter has always used it.
 * One digit finer than AC_TO_METERS2, so the two must not be collapsed.
 */
export const M2_PER_AC = 4046.8564224;
export const M2_TO_FT2 = 10.7639;
export const L_TO_GAL = 0.2641720524;
export const GAL_TO_L = 3.785411784;
/** Alias the builder's fuel price card already exported. */
export const L_PER_GAL = GAL_TO_L;

/** EPA / EIA factors. Match frontend/src/app/utils.js. */
export const GASOLINE_TONNES_CO2E_PER_LITRE = 0.002353757;
export const DIESEL_TONNES_CO2E_PER_LITRE = 0.002776423;

/**
 * A viewer's length and area units.
 * "default" (or a missing user setting) means the account's choice, which is what
 * appSlice has always done when a user signs in.
 * @param {Object=} userGeneral user settings.general
 * @param {Object=} customerGeneral account settings.general
 * @return {{unitsLength: *, unitsArea: *}}
 */
export function resolveDisplayUnits(userGeneral = {}, customerGeneral = {}) {
  const userLength = userGeneral && userGeneral.unitsLength;
  const userArea = userGeneral && userGeneral.unitsArea;
  const length =
    userLength == null || userLength === '' || userLength === 'default'
      ? customerGeneral && customerGeneral.unitsLength
      : userLength;
  const area =
    userArea == null || userArea === '' || userArea === 'default'
      ? customerGeneral && customerGeneral.unitsArea
      : userArea;
  return {unitsLength: length, unitsArea: area};
}

/**
 * True when the resolved length system is imperial (miles, feet, gallons).
 * Accounts store "imperial" / "metric"; a few callers pass the short unit instead.
 * @param {string} unitsLength
 * @return {boolean}
 */
export function isImperialLength(unitsLength) {
  return unitsLength === 'imperial' || unitsLength === 'mi' || unitsLength === 'ft';
}

/**
 * True when the resolved area system is hectares.
 * @param {string} unitsArea
 * @return {boolean}
 */
export function isHectareArea(unitsArea) {
  return unitsArea === 'hectare' || unitsArea === 'ha';
}

/**
 * @param {number|string} value
 * @param {string} outputUnits km, mi, m, ft, mph or kph. km/m/kph are the stored units.
 * @return {number|string}
 */
export function unitsLengthDisplayConversion(value, outputUnits) {
  if (value == '') {
    return value;
  } else if (outputUnits == 'mi' || outputUnits == 'mph') {
    return parseFloat(value * KM_TO_MI);
  } else if (outputUnits == 'km' || outputUnits == 'kph') {
    return parseFloat(value);
  } else if (outputUnits == 'ft') {
    return parseFloat(value * M_TO_FT);
  } else if (outputUnits == 'm') {
    return parseFloat(value);
  }
  console.error('outputUnits can be either "km", "mi", "m", "ft", "mph", "kph"');
  return value;
}

/**
 * @param {number} value
 * @param {string} outputUnits ha, ac or meters2
 * @param {string} [inputUnits]
 * @return {number}
 */
export function unitsAreaConversion(value, outputUnits, inputUnits = 'meters2') {
  if (value == '' || value == null || value == undefined || typeof value != 'number') {
    return value;
  }

  if (!(outputUnits == 'ha' || outputUnits == 'ac' || outputUnits == 'meters2')) {
    console.error('unitsAreaConversion: output units are not valid, can only be "ha", "ac", "meters2"');
    return value;
  }

  if (inputUnits == 'ha') {
    if (outputUnits == 'ac') {
      return value * HA_TO_AC;
    } else if (outputUnits == 'meters2') {
      return value * HA_TO_METERS2;
    }
    return value;
  } else if (inputUnits == 'ac') {
    if (outputUnits == 'ha') {
      return value * AC_TO_HA;
    } else if (outputUnits == 'meters2') {
      return value * AC_TO_METERS2;
    }
    return value;
  } else if (inputUnits == 'meters2') {
    if (outputUnits == 'ha') {
      return value / HA_TO_METERS2;
    } else if (outputUnits == 'ac') {
      return value / AC_TO_METERS2;
    }
    return value;
  }
  console.error('invalid inputUnits, can be "ha", "ac", "meters2"');
  return value;
}

/**
 * Litres to the viewer's fuel unit. Imperial shows gallons; metric leaves litres.
 * @param {number|string} value litres
 * @param {string} unitsLengthSystem 'imperial' or anything else
 * @return {number|string}
 */
export function unitsFuelDisplayConversion(value, unitsLengthSystem) {
  if (value === '' || value == null) return value;
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return unitsLengthSystem === 'imperial' ? n * L_TO_GAL : n;
}

/**
 * The unit words a measure is drawn with, for one resolved settings pair.
 * The engine keeps acres, miles, feet and litres; only labels and typed inputs convert.
 * @param {{unitsLength?: string, unitsArea?: string}=} units
 * @return {Object}
 */
export function displayUnits(units = {}) {
  const imperial = isImperialLength(units.unitsLength);
  const hectare = isHectareArea(units.unitsArea);
  return {
    imperial,
    hectare,
    area: hectare ? 'ha' : 'ac',
    areaWord: hectare ? 'hectares' : 'acres',
    length: imperial ? 'mi' : 'km',
    lengthWord: imperial ? 'miles' : 'kilometres',
    speed: imperial ? 'mph' : 'km/h',
    shortLength: imperial ? 'ft' : 'm',
    fuel: imperial ? 'gal' : 'L',
    fuelPrice: imperial ? '$/gal' : '$/L',
    perArea: hectare ? '/ha' : '/ac',
  };
}

/**
 * Stored acres to the viewer's area unit.
 * @param {number} acres
 * @param {Object} units displayUnits()
 * @return {number}
 */
export function acresForDisplay(acres, units) {
  if (!Number.isFinite(acres)) return acres;
  return units && units.hectare ? acres * AC_TO_HA : acres;
}

/**
 * Stored miles to the viewer's length unit.
 * @param {number} miles
 * @param {Object} units displayUnits()
 * @return {number}
 */
export function milesForDisplay(miles, units) {
  if (!Number.isFinite(miles)) return miles;
  return units && !units.imperial ? miles / KM_TO_MI : miles;
}

/**
 * Stored miles per hour to the viewer's speed unit.
 * @param {number} mph
 * @param {Object} units displayUnits()
 * @return {number}
 */
export function mphForDisplay(mph, units) {
  if (!Number.isFinite(mph)) return mph;
  return units && !units.imperial ? mph * MPH_TO_KPH : mph;
}

/**
 * Stored feet to the viewer's short length.
 * @param {number} feet
 * @param {Object} units displayUnits()
 * @return {number}
 */
export function feetForDisplay(feet, units) {
  if (!Number.isFinite(feet)) return feet;
  return units && !units.imperial ? feet / M_TO_FT : feet;
}

/**
 * Stored US gallons to the viewer's fuel unit.
 * @param {number} gallons
 * @param {Object} units displayUnits()
 * @return {number}
 */
export function gallonsForDisplay(gallons, units) {
  if (!Number.isFinite(gallons)) return gallons;
  return units && !units.imperial ? gallons * GAL_TO_L : gallons;
}
